// components/AdmissionLetter.tsx
import React from "react";
import { useSelector } from "react-redux";
import { AdmissionData } from "../admission-list";
import { selectBranches, selectCurrentBranch } from "../../../../core/data/redux/slice/branchesSlice";
import { absoluteCampusLogoUrl, campusBrandName, isOLevelAdmissionCampus, isEonAdmissionCampus } from "../../../../utils/campusLogos";
import StandardAdmissionLetterContent from "./StandardAdmissionLetterContent";
import OLevelAdmissionLetterContent from "./OLevelAdmissionLetterContent";
import EonAdmissionLetterContent from "./EonAdmissionLetterContent";

interface AdmissionLetterProps {
  admissionData: AdmissionData | null;
  onClose: () => void;
}

const AdmissionLetter: React.FC<AdmissionLetterProps> = ({
  admissionData,
  onClose,
}) => {
  // Prefer the admission's OWN campus over whatever's globally selected in
  // the header — those can differ (e.g. viewing a different campus's
  // student while your own header selection is elsewhere).
  const branches = useSelector(selectBranches);
  const currentBranch = useSelector(selectCurrentBranch);
  if (!admissionData) return null;
  const recordBranch = branches.find((b) => b._id === admissionData.branch) || currentBranch;

  const logoUrl = absoluteCampusLogoUrl(recordBranch?.logoUrl);
  const schoolName = campusBrandName(recordBranch).toUpperCase();
  const schoolAddress = recordBranch?.address || "";
  const isOLevel = isOLevelAdmissionCampus(recordBranch?.campusNo);
  const isEon = isEonAdmissionCampus(recordBranch?.campusNo);

  const currentDate = new Date();
  const formattedDate = `${currentDate
    .getDate()
    .toString()
    .padStart(2, "0")}-${(currentDate.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${currentDate.getFullYear()}`;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Admission Letter</h5>
            <button
              type="button"
              className="btn-close custom-btn-close"
              onClick={onClose}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="modal-body">
            {isEon ? (
              <EonAdmissionLetterContent
                admissionData={admissionData}
                logoUrl={logoUrl}
                schoolName={schoolName}
                schoolAddress={schoolAddress}
                formattedDate={formattedDate}
              />
            ) : isOLevel ? (
              <OLevelAdmissionLetterContent
                admissionData={admissionData}
                logoUrl={logoUrl}
                schoolName={schoolName}
                schoolAddress={schoolAddress}
                formattedDate={formattedDate}
              />
            ) : (
              <StandardAdmissionLetterContent
                admissionData={admissionData}
                logoUrl={logoUrl}
                schoolName={schoolName}
                schoolAddress={schoolAddress}
                formattedDate={formattedDate}
              />
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                const content = document.getElementById(
                  "admission-letter-content"
                );
                if (content) {
                  const printWindow = window.open("", "_blank");
                  if (printWindow) {
                    printWindow.document.write(`
                      <html>
                        <head>
                          <title>Admission Letter - ${admissionData.firstName} ${admissionData.lastName}</title>
                          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
                          <style>
                            body {
                              font-family: Arial, sans-serif;
                              margin: 20px;
                              color: #000;
                            }
                            .admission-letter {
                              max-width: 800px;
                              margin: 0 auto;
                            }
                            @media print {
                              body { margin: 0; }
                              .btn { display: none; }
                              .modal-footer { display: none; }
                            }
                            table {
                              width: 100%;
                              border-collapse: collapse;
                            }
                            table, th, td {
                              border: 1px solid #000;
                            }
                            th, td {
                              padding: 8px;
                              text-align: left;
                            }
                            .text-uppercase {
                              text-transform: uppercase;
                            }
                          </style>
                        </head>
                        <body>
                          ${content.innerHTML}
                          <script>
                            window.onload = function() {
                              window.print();
                              setTimeout(function() {
                                window.close();
                              }, 1000);
                            }
                          </script>
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                  }
                }
              }}
            >
              <i className="ti ti-printer me-2"></i>
              Print Letter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmissionLetter;
